"use client";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb";
import Layout from "@/lib/Layout";
import { ConvertLink, PersianNum } from "@/src/tools";
import { ProductGetType } from "@/src/types/product";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useRef, useState } from "react";
import { Autoplay, FreeMode, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import AttributePrices from "./components/attributes/AttributePrices";
import MainAttribute from "./components/attributes/MainAttribute";
import CommentList from "./components/comments/CommentList";
import DynamicImage from "./components/DynamicImageProduct";
import GalleryProduct from "./components/gallery/GalleryProduct";
import ProductDescription from "./components/ProductDescription";
import ProductFAQ from "./components/ProductFAQ";
import ProductVideo from "./components/ProductVideo";
import SellButton from "./components/SellButton";
import ProductSimilar from "./components/similar/ProductSimilar";
import { loadData } from "./tools";

export interface Slide {
  src: string;
  alt: string;
  width: number;
  height: number;
  productIdentityId?: number;
  type: "main" | "color";
}

const ProductView = ({ data, prodIID }: { data: ProductGetType, prodIID: number }) => {
  const product = data.data.product;
  const [selectedProdIID, setSelectedProdIID] = useState<number>(prodIID);
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const swiperMainRef = useRef<any>(null); // برای توقف اتوپلی

  const selectedIdentity = product.ProductIdentities?.find(
    (p) => p.id === selectedProdIID,
  );
  const wh = selectedIdentity?.Warehouses[0];
  const prop = selectedIdentity?.ProdProperties.map((p) => ({
    property: p.Property.property_name,
    attr: p.Attribute.attribute_name,
  }));

  interface Attribute {
    name: string;
    value: string;
  }

  const NormalAttribute: Attribute[] = useMemo(() => {
    const property = product.ProdProperties.map((i) => ({
      name: i.Property.property_name,
      value: i.Attribute.attribute_name,
    }));
    const result = Object.values(
      property.reduce((acc: Record<string, Attribute>, curr) => {
        if (acc[curr.name]) {
          acc[curr.name].value += ` , ${curr.value}`;
        } else {
          acc[curr.name] = { ...curr };
        }
        return acc;
      }, {})
    );
    return result as Attribute[];
  }, [product.ProdProperties]);

  const hexToRgba = (hex: string, opacity: number) => {
    let cleaned = hex.replace("#", "").trim();
    if (cleaned.length === 3) {
      cleaned = cleaned.split("").map((ch) => ch + ch).join("");
    }
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const mainAlbumImages = product.Albums || [];
  const colorAlbumsNested = product.ProductIdentities?.map((identity) => {
    const mainAlbum = identity.Albums?.filter((img) => img.album_main) || [];
    const otherAlbums = identity.Albums
      ?.filter((img) => !img.album_main)
      .sort((a, b) => (a.album_order || 0) - (b.album_order || 0)) || [];
    const sortedAlbums = [...mainAlbum, ...otherAlbums];
    return sortedAlbums.map((img) => ({
      src: process.env.IMAGE_URL + img.album_image_src,
      alt: img.album_image_alt || product.product_title,
      width: 1000,
      height: 1000,
      productIdentityId: identity.id,
      type: "color" as const,
    }));
  }) || [];
  const flatColorAlbums = colorAlbumsNested.flat();
  const mergedSlides: Slide[] = [
    ...mainAlbumImages.map((img) => ({
      src: process.env.IMAGE_URL + img.album_image_src,
      alt: img.album_image_alt || product.product_title,
      width: 1000,
      height: 1000,
      type: "main" as const,
    })),
    ...flatColorAlbums
  ];

  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const colorProduct = product.ProductIdentities?.find((o) => o.id === selectedProdIID);
  const selectColor = colorProduct?.ProdProperties.filter((o) => o.Property.property_name === "رنگ")[0];

  // slides به طوری که اول رنگ انتخابی بیاید
  const orderedSlides: Slide[] = useMemo(() => {
    if (selectedProdIID) {
      const colorSlides = mergedSlides.filter((s) => s.productIdentityId === selectedProdIID);
      const otherSlides = mergedSlides.filter((s) => s.productIdentityId !== selectedProdIID);
      return [...colorSlides, ...otherSlides];
    }
    return mergedSlides;
  }, [mergedSlides, selectedProdIID]);

  useMemo(() => {
    if (selectedSlideIndex >= orderedSlides.length) {
      setSelectedSlideIndex(0);
    }
  }, [orderedSlides, selectedSlideIndex]);

  const colorThumbnails = product.ProductIdentities?.map((identity) => {
    const album = identity.Albums?.find((img) => img.album_main == true);
    const attribute = identity.ProdProperties && identity.ProdProperties[0]?.Attribute;
    return {
      productIdentityId: identity.id,
      albumImage: album ? album.album_image_src : "",
      albumImageAlt: album ? album.album_image_alt : "",
      attributeName: attribute ? attribute.attribute_name : "",
      attributeLabel: attribute ? attribute.attribute_label : "",
      id: identity.id,
    };
  }) || [];

  // لوپ و توقف autoplay هنگام interaction (کلیک/سوایپ)
  const handleMainSwiper = (swiper: any) => {
    setSwiperInstance(swiper);
    swiperMainRef.current = swiper;
  };
  const stopAutoplay = () => {
    if (swiperMainRef.current?.autoplay) {
      swiperMainRef.current.autoplay.stop();
    }
  };

  const handleThumbnailClick = (prodIdentityId: number) => {
    setSelectedProdIID(prodIdentityId);
    setSelectedSlideIndex(0);
    swiperInstance?.slideTo(0);
    stopAutoplay();
  };

  const cat = product.ProdProperties?.filter((i) => i.Property.property_name === "دسته بندی")[0]?.Attribute;

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="relative mt-1">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <Link href="/">خانه</Link>
            </BreadcrumbItem>
            <span>/</span>
            <BreadcrumbItem>
              <Link href={`/categories/${ConvertLink(product.Category.cat_url)}`}>
                {product.Category.cat_title}
              </Link>
            </BreadcrumbItem>
            <span>/</span>
            <BreadcrumbItem>
              <BreadcrumbPage>{product.product_title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </nav>

      <article
        className="dark:bg-slate-600"
      >
        <div className="flex items-start justify-around flex-wrap max-w-4xl mx-auto">
          {/* ---- گالری محصول ---- */}
          <section className="sm:p-3 p-0 w-full sm:w-[50%]">
            <Swiper
              ref={swiperMainRef}
              loop={true}
              spaceBetween={20}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              speed={1000}
              pagination={{
                dynamicBullets: true,
              }}
              modules={[Autoplay, Pagination]}
              onSwiper={handleMainSwiper}
              onClick={stopAutoplay}
              onTouchStart={stopAutoplay}
              className="mySwiper"
            >
              {orderedSlides.map((slide, index) => (
                <SwiperSlide key={`${slide.src}-${index}`}>
                  <div
                    onClick={() => setLightboxIndex(index)}
                    className="cursor-pointer relative w-full h-[400px] sm:h-[500px] "
                    title={slide.alt}
                  >
                    <Image
                      fill
                      alt={slide.alt}
                      title={slide.alt}
                      src={slide.src}
                      sizes="(max-width: 600px) 100vw, 600px"
                      className="object-contain"
                      style={{ objectFit: "contain" }}
                      priority={index === 0}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <GalleryProduct
              slides={orderedSlides}
              index={lightboxIndex}
              setIndex={setLightboxIndex}
            />
          </section>

          <section className="w-full sm:p-3 p-1 sm:w-[50%]">
            <header className="flex justify-between my-1">
              <div>
                <h1 className="sm:text-lg text-sm font-bold">{product.product_title}</h1>
                <p className="text-xs text-slate-600" >{product.product_title_second}</p>
              </div>
              <span className="sm:w-1/4 w-1/2 text-left text-xs">امتیاز: {PersianNum(data?.data?.rating?.avgRate || 5)}</span>
            </header>

            {/* انتخاب رنگ */}
            <div className="flex items-center w-full my-1">
              <div
                className="flex-grow h-[1px]"
                style={{ backgroundColor: selectColor?.Attribute.attribute_label || "#000" }}
              />
              {selectColor && (
                <span className="mx-4 whitespace-nowrap text-sm text-black">
                  انتخاب رنگ :
                  <span className="font-bold"> {selectColor.Attribute.attribute_name}</span>
                </span>
              )}
              <div
                className="flex-grow h-[1px]"
                style={{ backgroundColor: selectColor?.Attribute.attribute_label || "#000" }}
              />
            </div>

            {/* لیست رنگ‌ها */}
            <div className="my-4" aria-label="انتخاب رنگ محصول">
              <Swiper
                loop={true}
                spaceBetween={10}
                freeMode={true}
                modules={[FreeMode]}
                breakpoints={{
                  0: { slidesPerView: 5 }
                }}
                className="colorThumbnailSwiper"
              >
                {colorThumbnails.map((thumb) => {
                  if (!thumb.albumImage) return null;
                  const isSelected = thumb.productIdentityId === selectedProdIID;
                  return (
                    <SwiperSlide key={thumb.id}>
                      <div
                        onClick={() => handleThumbnailClick(thumb.productIdentityId)}
                        role="button"
                        tabIndex={0}
                        aria-label={`انتخاب رنگ ${thumb.attributeName}`}
                        className="cursor-pointer"
                      >
                        <div className="relative cursor-pointer block w-[70px] h-[70px] pt-1">
                          <Image
                            height={70}
                            width={70}
                            alt={thumb.albumImageAlt || thumb.attributeName}
                            title={thumb.albumImageAlt || thumb.attributeName}
                            src={process.env.IMAGE_URL + thumb.albumImage}
                            className={clsx(
                              "rounded-md transition-all duration-200 object-cover")}
                            style={{
                              borderColor: thumb.attributeLabel.trim(),
                            }}
                          />
                        </div>
                        <div
                          className={clsx(
                            "text-center mt-[1px] mb-1 text-xs py-1 rounded-md flex items-center shadow justify-between px-2",
                            isSelected ? "bg-blue-400 text-white" : "bg-white text-black"
                          )}
                        >
                          <span
                            style={{
                              backgroundColor: isSelected
                                ? hexToRgba(thumb.attributeLabel.trim(), 0.9)
                                : hexToRgba(thumb.attributeLabel.trim(), 0.6),
                            }}
                            className="h-4 w-4 rounded-full block shadow-md"
                          />
                          <span>{thumb.attributeName}</span>
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>

            {/* دکمه خرید و قیمت */}
            <div className="flex items-end justify-between my-1">
              <SellButton
                ProductId={product.id}
                ProductIdentityId={selectedProdIID}
                unitPrice={wh?.warehouse_price ?? 0}
                unitDiscount={wh?.warehouse_discount ?? 0}
                available={wh?.warehouse_available ?? false}
                countWarehouse={wh?.warehouse_count ?? 0}
                property={prop}
              />
              <AttributePrices
                data={product.ProductIdentities}
                checkData={loadData(product)}
                prodIID={selectedProdIID}
              />
            </div>

            {/* اطلاعات برند و دسته */}
            <div className="my-2 flex justify-between flex-wrap p-2">
              <div className="flex items-center w-1/3">
                <span className="text-slate-800 dark:text-slate-400 text-sm">دسته بندی :</span>
                <Link
                  className="text-[#798b03] dark:text-slate-200 mx-1 text-sm"
                  href={`/categories/${ConvertLink(cat?.attribute_url ? cat?.attribute_url : product.Category.cat_url)}`}
                >
                  {cat?.attribute_name || product.Category.cat_title}
                </Link>
              </div>
              <div className="flex items-center sm:justify-center justify-end w-1/3">
                <span className="text-slate-800 dark:text-slate-400 text-sm">برند :</span>
                <Link
                  className="text-[#798b03] dark:text-slate-200 mx-1 text-sm"
                  href={`/brand/${ConvertLink(product.Brand.brand_url)}?page=1`}
                >
                  {product.Brand.brand_title}
                </Link>
              </div>
              <span className="w-1/3 text-left text-sm">تعداد دیدگاه: {PersianNum(data?.data?.rating?.totalComments || 0)}</span>
            </div>

            {/* ویژگی‌های محصول */}
            {product.product_detail_second && (
              <section className="p-1 w-full" aria-label="ویژگی‌های محصول">
                <div className="flex items-center w-full">
                  <div className="flex-grow h-[1px]" style={{ backgroundColor: "#000" }} />
                  <span className="mx-4 whitespace-nowrap text-sm">ویژگی</span>
                  <div className="flex-grow h-[1px]" style={{ backgroundColor: "#000" }} />
                </div>
                <div
                  className="pr-4"
                  dangerouslySetInnerHTML={{
                    __html: product.product_detail_second?.replace(/<br>/g, "")
                  }}
                />
              </section>
            )}
          </section>
        </div>

        {product.product_video && (
          <section aria-label="ویدیو محصول">
            <h2 className="sr-only">ویدیو محصول</h2>
            <ProductVideo
              product_video={product.product_video}
              product_video_detail={product.product_video_detail}
              product_video_title={product.product_video_title}
            />
          </section>
        )}

        <section aria-label="توضیحات محصول" className="my-6 max-w-4xl mx-auto">
          <h2 className="sr-only">توضیحات محصول</h2>
          <ProductDescription html={product.product_description} />
        </section>

        <MainAttribute data={NormalAttribute} />
        {
          product.ProductQAs.length ?
            <ProductFAQ qas={product.ProductQAs} productTitle={product.product_title} />
            : null
        }
        {product.product_image_alt_second && (
          <DynamicImage
            alt={product.product_image_alt_second}
            src={product.product_image_second}
            title={product.product_title}
          />
        )}

        {/* کامنت‌ها */}
        <CommentList productIId={prodIID} productId={product.id} />
        {/* محصولات مشابه */}
        <ProductSimilar productId={product.id} title={product.product_title} />
      </article>
    </Layout>

  );
};

export default ProductView;